/**
 * Дата и время одним полем: день — кликом в календаре, время — с клавиатуры «ЧЧ:ММ».
 *
 * Заменяет `input type="datetime-local"` в панели «Чатов» (02.10.26, замечание владельца:
 * «чтобы не было никакой часовой зоны… в календаре дату, а потом время — с клавиатуры»).
 * Нативное поле браузер рисует своим стилем, время в нём крутят сегментами, а Chrome ещё и
 * подписывает часовой пояс. Здесь пояса нет вовсе: значение — строка `ГГГГ-ММ-ДДTЧЧ:ММ`,
 * как у datetime-local, сервер читает её по-прежнему (`stage.дата_bitrix`), а часы и
 * минуты — те, что набрали, без пересчёта.
 *
 * Календарь — `RangeCalendar single`; попап и кнопка — свои, в стиле `Combo`. Черновик
 * живёт в попапе: применяется по Enter в поле времени или «Готово», закрытие мимо — без
 * изменений. Поле времени получает фокус при открытии и после клика по дню: «щёлкнул день —
 * набрал время — Enter» это одно движение.
 * Время можно не набирать (07.10.26, замечание владельца: клиент назвал день, а час ещё не
 * знает — записать было нельзя): день без времени уходит как `T00:00`. Полночь по всему
 * кабинету и значит «время не выбрано» — напоминание о примерке, шаблон адреса и загрузка
 * салона по часам читают её так же; в поле она подписана «без времени».
 * `compact` — подпись мелко над полем, а не в нём (панель расширения: дата целиком при 260 px).
 */
import { ChevronDown } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";

import { RangeCalendar } from "@/components/RangeCalendar";

const WEEKDAYS = ["вс", "пн", "вт", "ср", "чт", "пт", "сб"];

/** Время «не выбрано»: день без часа хранится полночью. */
const NO_TIME = "00:00";

/** `2026-10-03T15:00` → «пт 03.10.2026 15:00», полночь — «пт 03.10.2026 · без времени»; не дата — пусто. */
export function dateTimeLabel(value: string): string {
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(value)) return "";
  const d = new Date(Number(value.slice(0, 4)), Number(value.slice(5, 7)) - 1, Number(value.slice(8, 10)));
  const time = value.slice(11, 16);
  return `${WEEKDAYS[d.getDay()]} ${value.slice(8, 10)}.${value.slice(5, 7)}.${value.slice(0, 4)} ${time === NO_TIME ? "· без времени" : time}`;
}

/** Цифры набора → «Ч:ММ» / «ЧЧ:ММ». Первая цифра больше 2 — час однозначный: «930» → «9:30»,
 *  «1530» → «15:30». Двоеточие ставится само, человек набирает только цифры. */
export function maskTime(digits: string): string {
  const d = digits.replace(/\D/g, "");
  if (!d) return "";
  const split = d[0]! > "2" ? 1 : 2;
  const h = d.slice(0, split);
  const m = d.slice(split, split + 2);
  return m ? `${h}:${m}` : h;
}

/** Текст поля → `ЧЧ:ММ` для значения; не время (часов нет, 25:00, 12:70) — `null`.
 *  Одни часы («15») — ровный час: запись ставят по часам. */
export function parseTime(text: string): string | null {
  const m = /^(\d{1,2})(?::(\d{2}))?$/.exec(text.includes(":") ? text : maskTime(text));
  if (!m) return null;
  const h = Number(m[1]);
  const min = m[2] ?? "00";
  if (h > 23 || Number(min) > 59) return null;
  return `${String(h).padStart(2, "0")}:${min}`;
}

/** Правка поля времени → новый текст. С двоеточием часы и минуты правятся порознь
 *  (выделили часы, набрали «14» — минуты на месте); без него — набор с нуля через `maskTime`. */
export function editTime(raw: string): string {
  const colon = raw.indexOf(":");
  if (colon < 0) return maskTime(raw.replace(/\D/g, "").slice(0, 4));
  const h = raw.slice(0, colon).replace(/\D/g, "").slice(0, 2);
  const m = raw.slice(colon + 1).replace(/\D/g, "").slice(0, 2);
  return h || m ? `${h}:${m}` : "";
}

export function DateTimePick({
  label,
  value,
  onChange,
  required = false,
  placeholder = "выбрать…",
  compact = false,
}: {
  /** Подпись в кнопке: «Дата записи», «Следующая обработка». */
  label: string;
  /** `ГГГГ-ММ-ДДTЧЧ:ММ` или пустая строка. */
  value: string;
  onChange: (value: string) => void;
  /** Без «очистить»: дата записи в «Ждем клиента» обязательна. */
  required?: boolean;
  placeholder?: string;
  /** Тесное место (панель расширения, 260–320 px): подпись мелко над полем, поле ниже и во всю
   *  ширину — дата с временем видна целиком, подпись её не теснит. */
  compact?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState("");
  const [digits, setDigits] = useState("");
  const boxRef = useRef<HTMLDivElement>(null);
  const timeRef = useRef<HTMLInputElement>(null);
  const captionId = useId();

  // Выделить часы или минуты целиком — как сегмент: набранное заменяет его, второй остаётся.
  // Кадром позже: значение поля к этому моменту уже отрисовано.
  const selectPart = (part: "h" | "m") =>
    requestAnimationFrame(() => {
      const el = timeRef.current;
      if (!el) return;
      const colon = el.value.indexOf(":");
      if (colon < 0) el.select();
      else if (part === "h") el.setSelectionRange(0, colon);
      else el.setSelectionRange(colon + 1, el.value.length);
    });
  const focusHours = () => {
    timeRef.current?.focus();
    selectPart("h");
  };

  // Открылись — черновик из значения, фокус в поле времени, часы выделены.
  useEffect(() => {
    if (!open) return;
    setDate(value.slice(0, 10));
    // День без времени — поле времени пустое, а не «00:00».
    setDigits(value.length >= 16 && value.slice(11, 16) !== NO_TIME ? value.slice(11, 16) : "");
    focusHours();
  }, [open, value]);

  // Клик мимо и Escape закрывают без изменений. `mousedown`: щелчок по соседней кнопке
  // должен и закрыть попап, и сработать сам.
  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => {
      if (!boxRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const escape = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", away);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("mousedown", away);
      document.removeEventListener("keydown", escape);
    };
  }, [open]);

  const time = parseTime(digits);
  // Время не набрано — день без времени; набрано с ошибкой — ждём правки.
  const ready = Boolean(date) && (time !== null || !digits);
  const commit = () => {
    if (!ready) return;
    onChange(`${date}T${time ?? NO_TIME}`);
    setOpen(false);
  };
  const hint = !date
    ? "выберите день"
    : digits && time === null
      ? "время — ЧЧ:ММ, часы до 23"
      : !digits
        ? "наберите время — или «Готово»: день без времени"
        : "";
  const text = dateTimeLabel(value);

  return (
    <div ref={boxRef} className="relative" data-annot={`дата и время ${label}`}>
      {compact ? (
        <span id={captionId} className="mb-1 block text-[11px] leading-none text-fg-muted">
          {label}
        </span>
      ) : null}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title={text || label}
        // Подпись вынесена над кнопкой — для читалки сцепляем её с кнопкой явно.
        aria-labelledby={compact ? captionId : undefined}
        className={
          "flex w-full items-center overflow-hidden rounded-[var(--radius-ui)] border bg-bg text-[13px] " +
          (compact ? "h-7 " : "h-8 ") +
          (open ? "border-border-strong" : "border-border hover:border-border-strong")
        }
      >
        {compact ? null : (
          <span className="flex h-full flex-none items-center whitespace-nowrap border-r border-border bg-bg-subtle px-2 text-[11.5px] text-fg-faint">
            {label}
          </span>
        )}
        <span className={`truncate px-2 tabular-nums ${text ? "text-fg" : "text-fg-faint"}`}>{text || placeholder}</span>
        <ChevronDown size={13} className="mr-1.5 ml-auto flex-none text-fg-faint" />
      </button>

      {open ? (
        <div
          className={
            "absolute left-0 z-30 w-[264px] max-w-[calc(100vw-16px)] overflow-hidden rounded-[var(--radius-card)] border border-border bg-bg-elevated shadow-lg " +
            // Под кнопкой: в тесном виде над ней ещё подпись, поэтому не от фиксированной высоты.
            (compact ? "top-full mt-0.5" : "top-[34px]")
          }
        >
          <RangeCalendar
            from={date}
            to={date}
            single
            onPick={(day) => {
              setDate(day);
              focusHours();
            }}
          />
          {/* Не <form>: пикер стоит внутри формы создания сделки, а вложенная форма по Enter
              отправляла внешнюю нативно и перезагружала страницу (поймано 02.10 на живом прогоне). */}
          <div className="flex items-center gap-1.5 border-t border-border px-2 py-2">
            <span className="text-[11.5px] text-fg-faint">время</span>
            <input
              ref={timeRef}
              value={digits}
              onChange={(e) => {
                const raw = e.target.value;
                const colon = raw.indexOf(":");
                const next = editTime(raw);
                setDigits(next);
                // Набрали час целиком («14» или «9») — дальше минуты.
                const h = next.split(":")[0] ?? "";
                const typed = (e.nativeEvent as InputEvent).inputType?.startsWith("insert");
                if (typed && colon >= 0 && (e.target.selectionStart ?? 9) <= colon && (h.length === 2 || h > "2")) {
                  selectPart("m");
                }
              }}
              onFocus={() => selectPart("h")}
              onClick={(e) => {
                // Щелчок ставит курсор — выделяем сегмент под ним; протяжку мышью не трогаем.
                const el = e.currentTarget;
                if (el.selectionStart !== el.selectionEnd) return;
                selectPart((el.selectionStart ?? 0) <= el.value.indexOf(":") ? "h" : "m");
              }}
              onKeyDown={(e) => {
                if (e.key !== "Enter") return;
                e.preventDefault(); // иначе Enter отправит внешнюю форму
                commit();
              }}
              inputMode="numeric"
              placeholder="ЧЧ:ММ"
              aria-label={`${label}: время`}
              className={
                "h-7 w-[64px] rounded-[var(--radius-ui)] border bg-transparent px-2 text-center text-[13px] tabular-nums text-fg outline-none placeholder:text-fg-faint " +
                (digits && time === null ? "border-err" : "border-border focus:border-border-strong")
              }
            />
            <button
              type="button"
              onClick={commit}
              disabled={!ready}
              className="h-7 rounded-[var(--radius-ui)] border border-accent px-2 text-[12px] text-accent-text hover:bg-bg-hover disabled:opacity-50"
            >
              Готово
            </button>
            {!required && value ? (
              <button
                type="button"
                onClick={() => {
                  onChange("");
                  setOpen(false);
                }}
                className="ml-auto text-[11.5px] text-fg-faint hover:text-err"
              >
                очистить
              </button>
            ) : null}
          </div>
          {hint ? <div className="px-2 pb-2 text-[11px] text-fg-faint">{hint}</div> : null}
        </div>
      ) : null}
    </div>
  );
}

const MONTHS_OF = [
  "января", "февраля", "марта", "апреля", "мая", "июня",
  "июля", "августа", "сентября", "октября", "ноября", "декабря",
];

/** `2026-10-06` → «6 октября», чужой год — «6 октября 2027»; не дата — пусто. Только срезы строки:
 *  ни `Date`, ни пояса — день не уедет ни у кого, где бы ни стоял компьютер. */
export function dayLabel(value: string): string {
  if (!/^\d{4}-\d{2}-\d{2}/.test(value)) return "";
  const month = MONTHS_OF[Number(value.slice(5, 7)) - 1];
  if (!month) return "";
  const year = value.slice(0, 4);
  return `${Number(value.slice(8, 10))} ${month}${year === String(new Date().getFullYear()) ? "" : ` ${year}`}`;
}

/**
 * Одна дата без времени: кнопка «6 октября» и свой календарь (`RangeCalendar single`), клик по дню
 * сразу применяет. Заменяет `input type="date"` у даты мероприятия (05.10.26, замечание владельца:
 * операторы русскоязычные, а поле браузер рисует по своей локали — «11/12/2026»; «и чтобы таймзона
 * не работала»). Значение — строка `ГГГГ-ММ-ДД` как набрана, без пересчёта.
 */
export function DatePick({
  label,
  value,
  onChange,
  disabled = false,
  placeholder = "выбрать…",
  align = "left",
  big = false,
  id,
}: {
  /** Что за дата — для читалки и подсказки: «Дата мероприятия». */
  label: string;
  /** `ГГГГ-ММ-ДД` или пустая строка. */
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
  /** К какому краю кнопки прижат календарь: поле у правого края панели открывает его влево. */
  align?: "left" | "right";
  /** Дата — главное в блоке (даты проката у брони): крупнее и жирнее. */
  big?: boolean;
  id?: string;
}) {
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => {
      if (!boxRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const escape = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", away);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("mousedown", away);
      document.removeEventListener("keydown", escape);
    };
  }, [open]);
  const text = dayLabel(value);
  return (
    <div ref={boxRef} className="relative" data-annot={`дата ${label}`}>
      <button
        id={id}
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        title={text ? `${label}: ${text}` : label}
        aria-haspopup="dialog"
        aria-expanded={open}
        className={
          "flex w-full items-center whitespace-nowrap rounded-[var(--radius-ui)] border bg-bg px-2 disabled:opacity-50 " +
          (big ? "h-8 text-[15px] font-semibold " : "h-7 text-[12px] ") +
          (open ? "border-border-strong " : "border-border hover:border-border-strong ") +
          (text ? "text-fg" : "text-fg-faint")
        }
      >
        {text || placeholder}
      </button>
      {open ? (
        <div
          role="dialog"
          aria-label={label}
          className={
            "absolute top-full z-30 mt-0.5 w-[264px] max-w-[calc(100vw-16px)] overflow-hidden rounded-[var(--radius-card)] border border-border bg-bg-elevated shadow-lg " +
            (align === "right" ? "right-0" : "left-0")
          }
        >
          <RangeCalendar
            from={value}
            to={value}
            single
            onPick={(day) => {
              setOpen(false);
              if (day !== value) onChange(day);
            }}
          />
        </div>
      ) : null}
    </div>
  );
}
